import { Identifier } from "@nms-ng2/app/shared/models/basic.models";
import { v4 as uuid_v4 } from "uuid";
import { CircuitDirection } from "../../diagram/models/diagram.models";
import { Transmission } from "@nms-ng2/app/modules/topology/links/links.model";
import { LinkInfoCircuit } from "../../wizard/steps/4-links/links-step.model";

/**
 * Classe auxiliar para a conversão do modelo do diagrama.
 * Contém métodos úteis utilizados em mais de um conversor.
 */
export class DwdmDiagramConverterHelper {

    /**
     * Cria identificadores únicos para os elementos do diagrama.
     * São necessários principalmente para identificar as ligações entre os links.
     */
    static createId(): Identifier {
        return uuid_v4() as Identifier;
    }

    /**
     * Define a direção de um link, em relação ao circuito.
     *
     * Essa informação é importante  porque toda lógica da construção do diagrama
     * depende de saber se o link está indo do A-END para o Z-END (@see CircuitDirection.TOWARDS_Z_END)
     * ou se está no caminho inverso (@see CircuitDirection.TOWARDS_A_END).
     */
    static getLinkDirection(link: LinkInfoCircuit, circuitAEndResourceId: number, circuitZEndResourceId: number,
            intermediates: { resourceId: number }[]): CircuitDirection {
        return (DwdmDiagramConverterHelper.isLinkConnectedToSomeCircuitEnd(link, circuitAEndResourceId, circuitZEndResourceId))
            ? DwdmDiagramConverterHelper.getLinkDirectionBasedOnCircuitEnds(link, circuitAEndResourceId, circuitZEndResourceId)
            : DwdmDiagramConverterHelper.getElementDirectionBasedOnIntermediates(intermediates, link);
    }

    /**
     * Verifica se algum dos ends do link é algum dos ends do circuito, independente de direção.
     */
    static isLinkConnectedToSomeCircuitEnd(link: LinkInfoCircuit, circuitAEndResourceId: number, circuitZEndResourceId: number)
            : boolean {
        return link.aEndResourceId === circuitAEndResourceId
            || link.aEndResourceId === circuitZEndResourceId
            || link.zEndResourceId === circuitAEndResourceId
            || link.zEndResourceId === circuitZEndResourceId;
    }

    /**
     * Define a direção de um link, em relação ao circuito, considerando os ends do circuito.
     *
     * Essa informação é importante  porque toda lógica da construção do diagrama
     * depende de saber se o link está indo do A-END para o Z-END (@see CircuitDirection.TOWARDS_Z_END)
     * ou se está no caminho inverso (@see CircuitDirection.TOWARDS_A_END).
     */
    static getLinkDirectionBasedOnCircuitEnds(currentLink: LinkInfoCircuit, circuitAEndResourceId: number,
            circuitZEndResourceId: number): CircuitDirection | null {
        if (this.isLinkComingFromDevice(currentLink, circuitAEndResourceId)
                || this.isLinkGoingToDevice(currentLink, circuitZEndResourceId)) {
            return CircuitDirection.TOWARDS_Z_END;
        }

        if (this.isLinkComingFromDevice(currentLink, circuitZEndResourceId)
                || this.isLinkGoingToDevice(currentLink, circuitAEndResourceId)) {
            return CircuitDirection.TOWARDS_A_END;
        }

        this.logLinkDirectionError(currentLink.name, "One of the link ends should be a circuit end.");

        return null;
    }

    /**
     * Define a direção de um link, em relação ao circuito, considerando os intermediários do circuito.
     *
     * Essa informação é importante  porque toda lógica da construção do diagrama
     * depende de saber se o link está indo do A-END para o Z-END (@see CircuitDirection.TOWARDS_Z_END)
     * ou se está no caminho inverso (@see CircuitDirection.TOWARDS_A_END).
     */
    static getElementDirectionBasedOnIntermediates(intermediates: { resourceId: number }[], link: LinkInfoCircuit)
            : CircuitDirection | null {
        const sourceDevice = this.getSourceDevice(link);
        const targetDevice = this.getTargetDevice(link);

        const intermediateSourceIndex = intermediates.findIndex(intermediate => sourceDevice === intermediate.resourceId);
        const intermediateTargetIndex = intermediates.findIndex(intermediate => targetDevice === intermediate.resourceId);

        if (intermediateSourceIndex === -1 || intermediateTargetIndex === -1) {
            this.logLinkDirectionError(link.name, "One of the link ends should be an intermediate device.");

            return null;
        }

        return (intermediateSourceIndex < intermediateTargetIndex)
            ? CircuitDirection.TOWARDS_Z_END
            : CircuitDirection.TOWARDS_A_END
    }

    /**
     * Verifica se o link, em relação a um circuito, está saindo do equipamento especificado.
     *
     * Um link está saindo de um equipamento se algum dos ends do link é
     * o equipamento em questão e se a transmissão é TX.
     *
     * Importante lembrar que o A-End e Z-End do link não podem sozinhos indicar
     * a direção do link em relação ao circuito pois o link pode ser criado invertido.
     *
     * @param currentLink - O link a ser verificado.
     * @param deviceResourceId - O id do equipamento.
     * @returns true se o link está saindo do equipamento, caso contrário false.
     */
    static isLinkComingFromDevice(currentLink: LinkInfoCircuit, deviceResourceId: number) {
        return this.isLinkFromDeviceTx("aEnd", currentLink, deviceResourceId)
            || this.isLinkFromDeviceTx("zEnd", currentLink, deviceResourceId);
    }

    /**
     * Verifica se o link, em relação a um circuito, está indo para o equipamento especificado.
     *
     * Um link está indo para um equipamento se algum dos ends do link é
     * o equipamento em questão e se a transmissão é RX.
     *
     * Importante lembrar que o A-End e Z-End do link não podem sozinhos indicar
     * a direção do link em relação ao circuito pois o link pode ser criado invertido.
     *
     * @param currentLink - O link a ser verificado.
     * @param deviceResourceId - O id do equipamento.
     * @returns true se o link está indo para o equipamento, caso contrário false.
     */
    static isLinkGoingToDevice(currentLink: LinkInfoCircuit, deviceResourceId: number) {
        return this.isLinkFromDeviceRx("aEnd", currentLink, deviceResourceId)
            || this.isLinkFromDeviceRx("zEnd", currentLink, deviceResourceId);
    }

    /**
     * Verifica se o link está conectado ao equipamento especificado com transmissão TX.
     *
     * @param linkEndType - Tipo de extremidade do link (aEnd ou zEnd).
     * @param currentLink - O link a ser verificado.
     * @param deviceResourceId - O id do equipamento.
     * @returns true se o link está conectado ao equipamento com transmissão TX, caso contrário false.
     */
    static isLinkFromDeviceTx(linkEndType: "aEnd" | "zEnd", currentLink: LinkInfoCircuit, deviceResourceId: number) {
        return this.isLinkFromDeviceTransmission(linkEndType, currentLink, deviceResourceId, Transmission.TX);
    }

    /**
     * Verifica se o link está conectado ao equipamento especificado com transmissão RX.
     *
     * @param linkEndType - Tipo de extremidade do link (aEnd ou zEnd).
     * @param currentLink - O link a ser verificado.
     * @param deviceResourceId - O id do equipamento.
     * @returns true se o link está conectado ao equipamento com transmissão RX, caso contrário false.
     */
    static isLinkFromDeviceRx(linkEndType: "aEnd" | "zEnd", currentLink: LinkInfoCircuit, deviceResourceId: number) {
        return this.isLinkFromDeviceTransmission(linkEndType, currentLink, deviceResourceId, Transmission.RX);
    }

    /**
     * Retorna o equipamento origem do link em relação a um circuito.
     *
     * Importante lembrar que, no contexto de circuito,
     * não necessariamente o A-End do link é a origem do link
     * pois o link pode ter sido criado de forma invertida.
     * O que define a origem do link é ter a transmissão TX.
     *
     * @param currentLink - O link a ser verificado.
     * @returns O nome do equipamento origem ou undefined se não encontrado.
     */
    static getSourceDevice(currentLink: LinkInfoCircuit): number | undefined {
        return this.getDeviceByTransmission(currentLink, Transmission.TX);
    }

    /**
     * Retorna o equipamento destino do link em relação a um circuito.
     *
     * Importante lembrar que, no contexto de circuito,
     * não necessariamente o Z-End do link é o destino do link
     * pois o link pode ter sido criado de forma invertida.
     * O que define o destino do link é ter a transmissão RX.
     *
     * @param currentLink - O link a ser verificado.
     * @returns O nome do equipamento destino ou undefined se não encontrado.
     */
    static getTargetDevice(currentLink: LinkInfoCircuit): number | undefined {
        return this.getDeviceByTransmission(currentLink, Transmission.RX);
    }

    /**
     * Converte uma string de transmissão em seu valor correspondente na enum Transmission.
     *
     * @param transmission - A string representando a transmissão (ex: "TX", "RX").
     * @returns O valor da enum Transmission.
     */
    static getTransmission(transmission: string) {
        return Transmission[transmission];
    }

    /**
     * Verifica se o link está conectado a um equipamento específico com um tipo de transmissão definido.
     *
     * @param linkEndType - Tipo de extremidade do link (aEnd ou zEnd).
     * @param currentLink - O link a ser verificado.
     * @param deviceResourceId - O id do equipamento.
     * @param transmission - O tipo de transmissão (TX ou RX).
     * @returns true se o link está conectado ao equipamento com o tipo de transmissão especificado, caso contrário false.
     */
    private static isLinkFromDeviceTransmission(linkEndType: "aEnd" | "zEnd", currentLink: LinkInfoCircuit,
            deviceResourceId: number, transmission: Transmission) {
        const linkEndResourceId = (linkEndType === "aEnd") ? currentLink.aEndResourceId : currentLink.zEndResourceId;
        const linkTransmission = (linkEndType === "aEnd") ? currentLink.aEndTransmission : currentLink.zEndTransmission;

        return linkEndResourceId === deviceResourceId && linkTransmission === transmission;
    }

    /**
     * Retorna o equipamento associado a uma transmissão específica no link.
     *
     * @param currentLink - O link a ser verificado.
     * @param transmission - O tipo de transmissão (TX ou RX).
     * @returns O id do equipamento ou undefined se não encontrado.
     */
    private static getDeviceByTransmission(currentLink: LinkInfoCircuit, transmission: Transmission): number | undefined {
        if (currentLink.aEndTransmission === transmission) {
            return currentLink.aEndResourceId;
        }

        if (currentLink.zEndTransmission === transmission) {
            return currentLink.zEndResourceId;
        }

        return undefined;
    }

    /**
     * Registra um erro de direção do link no console.
     *
     * @param linkName - O nome do link com problema.
     * @param details - Detalhes adicionais sobre o erro.
     */
    private static logLinkDirectionError(linkName: string, details: string) {
        console.error(`It was not possible to define Link '${linkName}' direction. ${details}. Verify circuit configuration.`);
    }
}
